/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteProgramPathParameters,
  GetPresignedUrlParams,
  GetPresignedUrlPathParameters,
  GetProgramParams,
  GetProgramPathParameters,
  GetProgramPreviewParams,
  GetProgramPreviewPathParameters,
  GetProgramVersionHistoryPathParameters,
  ListProgramsParams,
  RenameProgramPathParameters,
  SaveProgramPathParameters,
  SearchProgramPreviewParams,
  SearchProgramPreviewPathParameters,
  TLBaseExecutorRequest,
  TLBulkDeleteRequest,
  TLBulkDeleteResponse,
  TLCreateProgramRequest,
  TLCreateProgramResponse,
  TLDownloadUrlResponse,
  TLHTTPValidationError,
  TLPreviewResponse,
  TLPreviewSearchResponse,
  TLProgram,
  TLProgramOperationResponse,
  TLProgramUploadResponse,
  TLProgramVersionHistoryResponse,
  TLRenameProgramRequest,
  TLSaveProgramRequest,
  TLSaveProgramResponse,
  TLUpdateProgramRequest,
  TLUpdateProgramResponse,
  TLUploadAbortRequest,
  TLUploadAbortResponse,
  TLUploadCompleteRequest,
  TLUploadCompleteResponse,
  TLUploadInitiateRequest,
  TLUploadInitiateResponse,
  TLUploadProgramsRequest,
  UpdateProgramPathParameters,
  UploadProgramsParams,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * List all programs, optionally filtered by domain.
 * @summary List Programs
 */
export const listPrograms = (
  params?: ListProgramsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProgram[]>({ url: `/programs`, method: 'GET', params, signal }, options);
};

export const getListProgramsQueryKey = (params?: ListProgramsParams) => {
  return ['programs', ...(params ? [params] : [])] as const;
};

export const getListProgramsQueryOptions = <
  TData = Awaited<ReturnType<typeof listPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListProgramsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listPrograms>>> = ({ signal }) =>
    listPrograms(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listPrograms>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListProgramsQueryResult = NonNullable<Awaited<ReturnType<typeof listPrograms>>>;
export type ListProgramsQueryError = ErrorType<TLHTTPValidationError>;

export function useListPrograms<
  TData = Awaited<ReturnType<typeof listPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListProgramsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPrograms>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPrograms>>,
          TError,
          Awaited<ReturnType<typeof listPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPrograms<
  TData = Awaited<ReturnType<typeof listPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPrograms>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPrograms>>,
          TError,
          Awaited<ReturnType<typeof listPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPrograms<
  TData = Awaited<ReturnType<typeof listPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Programs
 */

export function useListPrograms<
  TData = Awaited<ReturnType<typeof listPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListProgramsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Programs
 */
export const prefetchListProgramsQuery = async <
  TData = Awaited<ReturnType<typeof listPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListProgramsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Programs
 */
export const invalidateListPrograms = async (
  queryClient: QueryClient,
  params?: ListProgramsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListProgramsQueryKey(params) }, options);

  return queryClient;
};

/**
 * Create a new program.
 * @summary Create Program
 */
export const createProgram = (
  tLCreateProgramRequest: BodyType<TLCreateProgramRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCreateProgramResponse>(
    {
      url: `/programs`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreateProgramRequest,
      signal,
    },
    options,
  );
};

export const getCreateProgramQueryKey = (tLCreateProgramRequest?: BodyType<TLCreateProgramRequest>) => {
  return ['POST', 'programs', tLCreateProgramRequest] as const;
};

export const getCreateProgramQueryOptions = <
  TData = Awaited<ReturnType<typeof createProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProgramRequest: BodyType<TLCreateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateProgramQueryKey(tLCreateProgramRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createProgram>>> = ({ signal }) =>
    createProgram(tLCreateProgramRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createProgram>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateProgramQueryResult = NonNullable<Awaited<ReturnType<typeof createProgram>>>;
export type CreateProgramQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateProgram<
  TData = Awaited<ReturnType<typeof createProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProgramRequest: BodyType<TLCreateProgramRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProgram>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createProgram>>,
          TError,
          Awaited<ReturnType<typeof createProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateProgram<
  TData = Awaited<ReturnType<typeof createProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProgramRequest: BodyType<TLCreateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProgram>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createProgram>>,
          TError,
          Awaited<ReturnType<typeof createProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateProgram<
  TData = Awaited<ReturnType<typeof createProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProgramRequest: BodyType<TLCreateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Program
 */

export function useCreateProgram<
  TData = Awaited<ReturnType<typeof createProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProgramRequest: BodyType<TLCreateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateProgramQueryOptions(tLCreateProgramRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Program
 */
export const prefetchCreateProgramQuery = async <
  TData = Awaited<ReturnType<typeof createProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCreateProgramRequest: BodyType<TLCreateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateProgramQueryOptions(tLCreateProgramRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Program
 */
export const invalidateCreateProgram = async (
  queryClient: QueryClient,
  tLCreateProgramRequest: BodyType<TLCreateProgramRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateProgramQueryKey(tLCreateProgramRequest) }, options);

  return queryClient;
};

/**
 * @summary Bulk Delete Programs
 */
export const bulkDeletePrograms = (
  tLBulkDeleteRequest: BodyType<TLBulkDeleteRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkDeleteResponse>(
    {
      url: `/programs/bulk-delete`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      data: tLBulkDeleteRequest,
      signal,
    },
    options,
  );
};

export const getBulkDeleteProgramsQueryKey = (tLBulkDeleteRequest?: BodyType<TLBulkDeleteRequest>) => {
  return ['DELETE', 'programs', 'bulk-delete', tLBulkDeleteRequest] as const;
};

export const getBulkDeleteProgramsQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkDeletePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteRequest: BodyType<TLBulkDeleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkDeletePrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkDeleteProgramsQueryKey(tLBulkDeleteRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkDeletePrograms>>> = ({ signal }) =>
    bulkDeletePrograms(tLBulkDeleteRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkDeletePrograms>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkDeleteProgramsQueryResult = NonNullable<Awaited<ReturnType<typeof bulkDeletePrograms>>>;
export type BulkDeleteProgramsQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkDeletePrograms<
  TData = Awaited<ReturnType<typeof bulkDeletePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteRequest: BodyType<TLBulkDeleteRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkDeletePrograms>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkDeletePrograms>>,
          TError,
          Awaited<ReturnType<typeof bulkDeletePrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkDeletePrograms<
  TData = Awaited<ReturnType<typeof bulkDeletePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteRequest: BodyType<TLBulkDeleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkDeletePrograms>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkDeletePrograms>>,
          TError,
          Awaited<ReturnType<typeof bulkDeletePrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkDeletePrograms<
  TData = Awaited<ReturnType<typeof bulkDeletePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteRequest: BodyType<TLBulkDeleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkDeletePrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Delete Programs
 */

export function useBulkDeletePrograms<
  TData = Awaited<ReturnType<typeof bulkDeletePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteRequest: BodyType<TLBulkDeleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkDeletePrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkDeleteProgramsQueryOptions(tLBulkDeleteRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Delete Programs
 */
export const prefetchBulkDeleteProgramsQuery = async <
  TData = Awaited<ReturnType<typeof bulkDeletePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLBulkDeleteRequest: BodyType<TLBulkDeleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkDeletePrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkDeleteProgramsQueryOptions(tLBulkDeleteRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Delete Programs
 */
export const invalidateBulkDeletePrograms = async (
  queryClient: QueryClient,
  tLBulkDeleteRequest: BodyType<TLBulkDeleteRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getBulkDeleteProgramsQueryKey(tLBulkDeleteRequest) }, options);

  return queryClient;
};

/**
 * @summary Execute Program
 */
export const executeProgram = (
  tLBaseExecutorRequest: BodyType<TLBaseExecutorRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProgram[]>(
    {
      url: `/programs/execute`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLBaseExecutorRequest,
      signal,
    },
    options,
  );
};

export const getExecuteProgramQueryKey = (tLBaseExecutorRequest?: BodyType<TLBaseExecutorRequest>) => {
  return ['POST', 'programs', 'execute', tLBaseExecutorRequest] as const;
};

export const getExecuteProgramQueryOptions = <
  TData = Awaited<ReturnType<typeof executeProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBaseExecutorRequest: BodyType<TLBaseExecutorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getExecuteProgramQueryKey(tLBaseExecutorRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof executeProgram>>> = ({ signal }) =>
    executeProgram(tLBaseExecutorRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof executeProgram>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ExecuteProgramQueryResult = NonNullable<Awaited<ReturnType<typeof executeProgram>>>;
export type ExecuteProgramQueryError = ErrorType<TLHTTPValidationError>;

export function useExecuteProgram<
  TData = Awaited<ReturnType<typeof executeProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBaseExecutorRequest: BodyType<TLBaseExecutorRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeProgram>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeProgram>>,
          TError,
          Awaited<ReturnType<typeof executeProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteProgram<
  TData = Awaited<ReturnType<typeof executeProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBaseExecutorRequest: BodyType<TLBaseExecutorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeProgram>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeProgram>>,
          TError,
          Awaited<ReturnType<typeof executeProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteProgram<
  TData = Awaited<ReturnType<typeof executeProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBaseExecutorRequest: BodyType<TLBaseExecutorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Execute Program
 */

export function useExecuteProgram<
  TData = Awaited<ReturnType<typeof executeProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBaseExecutorRequest: BodyType<TLBaseExecutorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExecuteProgramQueryOptions(tLBaseExecutorRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Execute Program
 */
export const prefetchExecuteProgramQuery = async <
  TData = Awaited<ReturnType<typeof executeProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLBaseExecutorRequest: BodyType<TLBaseExecutorRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExecuteProgramQueryOptions(tLBaseExecutorRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Execute Program
 */
export const invalidateExecuteProgram = async (
  queryClient: QueryClient,
  tLBaseExecutorRequest: BodyType<TLBaseExecutorRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getExecuteProgramQueryKey(tLBaseExecutorRequest) }, options);

  return queryClient;
};

/**
 * Upload one or more programs using streaming to avoid loading into memory.
 * @summary Upload Programs
 */
export const uploadPrograms = (
  tLUploadProgramsRequest: BodyType<TLUploadProgramsRequest>,
  params: UploadProgramsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  tLUploadProgramsRequest.files.forEach((value) => formData.append(`files`, value));

  return apiInstance<TLProgramUploadResponse>(
    {
      url: `/programs/upload`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      params,
      signal,
    },
    options,
  );
};

export const getUploadProgramsQueryKey = (
  tLUploadProgramsRequest?: BodyType<TLUploadProgramsRequest>,
  params?: UploadProgramsParams,
) => {
  return ['POST', 'programs', 'upload', ...(params ? [params] : []), tLUploadProgramsRequest] as const;
};

export const getUploadProgramsQueryOptions = <
  TData = Awaited<ReturnType<typeof uploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadProgramsRequest: BodyType<TLUploadProgramsRequest>,
  params: UploadProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUploadProgramsQueryKey(tLUploadProgramsRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof uploadPrograms>>> = ({ signal }) =>
    uploadPrograms(tLUploadProgramsRequest, params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof uploadPrograms>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UploadProgramsQueryResult = NonNullable<Awaited<ReturnType<typeof uploadPrograms>>>;
export type UploadProgramsQueryError = ErrorType<TLHTTPValidationError>;

export function useUploadPrograms<
  TData = Awaited<ReturnType<typeof uploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadProgramsRequest: BodyType<TLUploadProgramsRequest>,
  params: UploadProgramsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadPrograms>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof uploadPrograms>>,
          TError,
          Awaited<ReturnType<typeof uploadPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUploadPrograms<
  TData = Awaited<ReturnType<typeof uploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadProgramsRequest: BodyType<TLUploadProgramsRequest>,
  params: UploadProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadPrograms>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof uploadPrograms>>,
          TError,
          Awaited<ReturnType<typeof uploadPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUploadPrograms<
  TData = Awaited<ReturnType<typeof uploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadProgramsRequest: BodyType<TLUploadProgramsRequest>,
  params: UploadProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Upload Programs
 */

export function useUploadPrograms<
  TData = Awaited<ReturnType<typeof uploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadProgramsRequest: BodyType<TLUploadProgramsRequest>,
  params: UploadProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUploadProgramsQueryOptions(tLUploadProgramsRequest, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Upload Programs
 */
export const prefetchUploadProgramsQuery = async <
  TData = Awaited<ReturnType<typeof uploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLUploadProgramsRequest: BodyType<TLUploadProgramsRequest>,
  params: UploadProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUploadProgramsQueryOptions(tLUploadProgramsRequest, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Upload Programs
 */
export const invalidateUploadPrograms = async (
  queryClient: QueryClient,
  tLUploadProgramsRequest: BodyType<TLUploadProgramsRequest>,
  params: UploadProgramsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUploadProgramsQueryKey(tLUploadProgramsRequest, params) },
    options,
  );

  return queryClient;
};

/**
 * Abort an upload and soft-delete the program.
 * @summary Abort Upload
 */
export const abortUpload = (
  tLUploadAbortRequest: BodyType<TLUploadAbortRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUploadAbortResponse>(
    {
      url: `/programs/upload/abort`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLUploadAbortRequest,
      signal,
    },
    options,
  );
};

export const getAbortUploadQueryKey = (tLUploadAbortRequest?: BodyType<TLUploadAbortRequest>) => {
  return ['POST', 'programs', 'upload', 'abort', tLUploadAbortRequest] as const;
};

export const getAbortUploadQueryOptions = <
  TData = Awaited<ReturnType<typeof abortUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadAbortRequest: BodyType<TLUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAbortUploadQueryKey(tLUploadAbortRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof abortUpload>>> = ({ signal }) =>
    abortUpload(tLUploadAbortRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof abortUpload>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AbortUploadQueryResult = NonNullable<Awaited<ReturnType<typeof abortUpload>>>;
export type AbortUploadQueryError = ErrorType<TLHTTPValidationError>;

export function useAbortUpload<
  TData = Awaited<ReturnType<typeof abortUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadAbortRequest: BodyType<TLUploadAbortRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUpload>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof abortUpload>>,
          TError,
          Awaited<ReturnType<typeof abortUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAbortUpload<
  TData = Awaited<ReturnType<typeof abortUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadAbortRequest: BodyType<TLUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUpload>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof abortUpload>>,
          TError,
          Awaited<ReturnType<typeof abortUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAbortUpload<
  TData = Awaited<ReturnType<typeof abortUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadAbortRequest: BodyType<TLUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Abort Upload
 */

export function useAbortUpload<
  TData = Awaited<ReturnType<typeof abortUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadAbortRequest: BodyType<TLUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAbortUploadQueryOptions(tLUploadAbortRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Abort Upload
 */
export const prefetchAbortUploadQuery = async <
  TData = Awaited<ReturnType<typeof abortUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLUploadAbortRequest: BodyType<TLUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAbortUploadQueryOptions(tLUploadAbortRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Abort Upload
 */
export const invalidateAbortUpload = async (
  queryClient: QueryClient,
  tLUploadAbortRequest: BodyType<TLUploadAbortRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getAbortUploadQueryKey(tLUploadAbortRequest) }, options);

  return queryClient;
};

/**
 * Complete an upload and activate the program.
 * @summary Complete Upload
 */
export const completeUpload = (
  tLUploadCompleteRequest: BodyType<TLUploadCompleteRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUploadCompleteResponse>(
    {
      url: `/programs/upload/complete`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLUploadCompleteRequest,
      signal,
    },
    options,
  );
};

export const getCompleteUploadQueryKey = (tLUploadCompleteRequest?: BodyType<TLUploadCompleteRequest>) => {
  return ['POST', 'programs', 'upload', 'complete', tLUploadCompleteRequest] as const;
};

export const getCompleteUploadQueryOptions = <
  TData = Awaited<ReturnType<typeof completeUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadCompleteRequest: BodyType<TLUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCompleteUploadQueryKey(tLUploadCompleteRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof completeUpload>>> = ({ signal }) =>
    completeUpload(tLUploadCompleteRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof completeUpload>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CompleteUploadQueryResult = NonNullable<Awaited<ReturnType<typeof completeUpload>>>;
export type CompleteUploadQueryError = ErrorType<TLHTTPValidationError>;

export function useCompleteUpload<
  TData = Awaited<ReturnType<typeof completeUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadCompleteRequest: BodyType<TLUploadCompleteRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUpload>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof completeUpload>>,
          TError,
          Awaited<ReturnType<typeof completeUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCompleteUpload<
  TData = Awaited<ReturnType<typeof completeUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadCompleteRequest: BodyType<TLUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUpload>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof completeUpload>>,
          TError,
          Awaited<ReturnType<typeof completeUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCompleteUpload<
  TData = Awaited<ReturnType<typeof completeUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadCompleteRequest: BodyType<TLUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Complete Upload
 */

export function useCompleteUpload<
  TData = Awaited<ReturnType<typeof completeUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadCompleteRequest: BodyType<TLUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCompleteUploadQueryOptions(tLUploadCompleteRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Complete Upload
 */
export const prefetchCompleteUploadQuery = async <
  TData = Awaited<ReturnType<typeof completeUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLUploadCompleteRequest: BodyType<TLUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCompleteUploadQueryOptions(tLUploadCompleteRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Complete Upload
 */
export const invalidateCompleteUpload = async (
  queryClient: QueryClient,
  tLUploadCompleteRequest: BodyType<TLUploadCompleteRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCompleteUploadQueryKey(tLUploadCompleteRequest) }, options);

  return queryClient;
};

/**
 * Initiate direct-to-storage uploads via presigned URLs.
 * @summary Initiate Upload Programs
 */
export const initiateUploadPrograms = (
  tLUploadInitiateRequest: BodyType<TLUploadInitiateRequest[]>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUploadInitiateResponse>(
    {
      url: `/programs/upload/init`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLUploadInitiateRequest,
      signal,
    },
    options,
  );
};

export const getInitiateUploadProgramsQueryKey = (tLUploadInitiateRequest?: BodyType<TLUploadInitiateRequest[]>) => {
  return ['POST', 'programs', 'upload', 'init', tLUploadInitiateRequest] as const;
};

export const getInitiateUploadProgramsQueryOptions = <
  TData = Awaited<ReturnType<typeof initiateUploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadInitiateRequest: BodyType<TLUploadInitiateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUploadPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getInitiateUploadProgramsQueryKey(tLUploadInitiateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof initiateUploadPrograms>>> = ({ signal }) =>
    initiateUploadPrograms(tLUploadInitiateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof initiateUploadPrograms>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type InitiateUploadProgramsQueryResult = NonNullable<Awaited<ReturnType<typeof initiateUploadPrograms>>>;
export type InitiateUploadProgramsQueryError = ErrorType<TLHTTPValidationError>;

export function useInitiateUploadPrograms<
  TData = Awaited<ReturnType<typeof initiateUploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadInitiateRequest: BodyType<TLUploadInitiateRequest[]>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUploadPrograms>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof initiateUploadPrograms>>,
          TError,
          Awaited<ReturnType<typeof initiateUploadPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useInitiateUploadPrograms<
  TData = Awaited<ReturnType<typeof initiateUploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadInitiateRequest: BodyType<TLUploadInitiateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUploadPrograms>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof initiateUploadPrograms>>,
          TError,
          Awaited<ReturnType<typeof initiateUploadPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useInitiateUploadPrograms<
  TData = Awaited<ReturnType<typeof initiateUploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadInitiateRequest: BodyType<TLUploadInitiateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUploadPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Initiate Upload Programs
 */

export function useInitiateUploadPrograms<
  TData = Awaited<ReturnType<typeof initiateUploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLUploadInitiateRequest: BodyType<TLUploadInitiateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUploadPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getInitiateUploadProgramsQueryOptions(tLUploadInitiateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Initiate Upload Programs
 */
export const prefetchInitiateUploadProgramsQuery = async <
  TData = Awaited<ReturnType<typeof initiateUploadPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLUploadInitiateRequest: BodyType<TLUploadInitiateRequest[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUploadPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getInitiateUploadProgramsQueryOptions(tLUploadInitiateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Initiate Upload Programs
 */
export const invalidateInitiateUploadPrograms = async (
  queryClient: QueryClient,
  tLUploadInitiateRequest: BodyType<TLUploadInitiateRequest[]>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getInitiateUploadProgramsQueryKey(tLUploadInitiateRequest) },
    options,
  );

  return queryClient;
};

/**
 * Delete a specific program.
 * @summary Delete Program
 */
export const deleteProgram = (
  { programId }: DeleteProgramPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProgramOperationResponse>({ url: `/programs/${programId}`, method: 'DELETE', signal }, options);
};

export const getDeleteProgramQueryKey = ({ programId }: DeleteProgramPathParameters) => {
  return ['DELETE', 'programs', programId] as const;
};

export const getDeleteProgramQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: DeleteProgramPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteProgramQueryKey({ programId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteProgram>>> = ({ signal }) =>
    deleteProgram({ programId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteProgram>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteProgramQueryResult = NonNullable<Awaited<ReturnType<typeof deleteProgram>>>;
export type DeleteProgramQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteProgram<
  TData = Awaited<ReturnType<typeof deleteProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteProgramPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProgram>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteProgram>>,
          TError,
          Awaited<ReturnType<typeof deleteProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteProgram<
  TData = Awaited<ReturnType<typeof deleteProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteProgramPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProgram>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteProgram>>,
          TError,
          Awaited<ReturnType<typeof deleteProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteProgram<
  TData = Awaited<ReturnType<typeof deleteProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteProgramPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Program
 */

export function useDeleteProgram<
  TData = Awaited<ReturnType<typeof deleteProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: DeleteProgramPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteProgramQueryOptions({ programId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Program
 */
export const prefetchDeleteProgramQuery = async <
  TData = Awaited<ReturnType<typeof deleteProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: DeleteProgramPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteProgramQueryOptions({ programId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Program
 */
export const invalidateDeleteProgram = async (
  queryClient: QueryClient,
  { programId }: DeleteProgramPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteProgramQueryKey({ programId }) }, options);

  return queryClient;
};

/**
 * @summary Get Program
 */
export const getProgram = (
  { programId }: GetProgramPathParameters,
  params?: GetProgramParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProgram>({ url: `/programs/${programId}`, method: 'GET', params, signal }, options);
};

export const getGetProgramQueryKey = ({ programId }: GetProgramPathParameters, params?: GetProgramParams) => {
  return ['programs', programId, ...(params ? [params] : [])] as const;
};

export const getGetProgramQueryOptions = <
  TData = Awaited<ReturnType<typeof getProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetProgramPathParameters,
  params?: GetProgramParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProgramQueryKey({ programId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProgram>>> = ({ signal }) =>
    getProgram({ programId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getProgram>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetProgramQueryResult = NonNullable<Awaited<ReturnType<typeof getProgram>>>;
export type GetProgramQueryError = ErrorType<TLHTTPValidationError>;

export function useGetProgram<
  TData = Awaited<ReturnType<typeof getProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramPathParameters,
  params: undefined | GetProgramParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgram>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgram>>,
          TError,
          Awaited<ReturnType<typeof getProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProgram<
  TData = Awaited<ReturnType<typeof getProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramPathParameters,
  params?: GetProgramParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgram>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgram>>,
          TError,
          Awaited<ReturnType<typeof getProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProgram<
  TData = Awaited<ReturnType<typeof getProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramPathParameters,
  params?: GetProgramParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Program
 */

export function useGetProgram<
  TData = Awaited<ReturnType<typeof getProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetProgramPathParameters,
  params?: GetProgramParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProgramQueryOptions({ programId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Program
 */
export const prefetchGetProgramQuery = async <
  TData = Awaited<ReturnType<typeof getProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: GetProgramPathParameters,
  params?: GetProgramParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetProgramQueryOptions({ programId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Program
 */
export const invalidateGetProgram = async (
  queryClient: QueryClient,
  { programId }: GetProgramPathParameters,
  params?: GetProgramParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetProgramQueryKey({ programId }, params) }, options);

  return queryClient;
};

/**
 * Save/update the contents of a program.
 * @summary Save Program
 */
export const saveProgram = (
  { programId }: SaveProgramPathParameters,
  tLSaveProgramRequest: BodyType<TLSaveProgramRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSaveProgramResponse>(
    {
      url: `/programs/${programId}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSaveProgramRequest,
      signal,
    },
    options,
  );
};

export const getSaveProgramQueryKey = (
  { programId }: SaveProgramPathParameters,
  tLSaveProgramRequest?: BodyType<TLSaveProgramRequest>,
) => {
  return ['POST', 'programs', programId, tLSaveProgramRequest] as const;
};

export const getSaveProgramQueryOptions = <
  TData = Awaited<ReturnType<typeof saveProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: SaveProgramPathParameters,
  tLSaveProgramRequest: BodyType<TLSaveProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSaveProgramQueryKey({ programId }, tLSaveProgramRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof saveProgram>>> = ({ signal }) =>
    saveProgram({ programId }, tLSaveProgramRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof saveProgram>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SaveProgramQueryResult = NonNullable<Awaited<ReturnType<typeof saveProgram>>>;
export type SaveProgramQueryError = ErrorType<TLHTTPValidationError>;

export function useSaveProgram<
  TData = Awaited<ReturnType<typeof saveProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SaveProgramPathParameters,
  tLSaveProgramRequest: BodyType<TLSaveProgramRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveProgram>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof saveProgram>>,
          TError,
          Awaited<ReturnType<typeof saveProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSaveProgram<
  TData = Awaited<ReturnType<typeof saveProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SaveProgramPathParameters,
  tLSaveProgramRequest: BodyType<TLSaveProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveProgram>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof saveProgram>>,
          TError,
          Awaited<ReturnType<typeof saveProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSaveProgram<
  TData = Awaited<ReturnType<typeof saveProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SaveProgramPathParameters,
  tLSaveProgramRequest: BodyType<TLSaveProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Save Program
 */

export function useSaveProgram<
  TData = Awaited<ReturnType<typeof saveProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: SaveProgramPathParameters,
  tLSaveProgramRequest: BodyType<TLSaveProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSaveProgramQueryOptions({ programId }, tLSaveProgramRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Save Program
 */
export const prefetchSaveProgramQuery = async <
  TData = Awaited<ReturnType<typeof saveProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: SaveProgramPathParameters,
  tLSaveProgramRequest: BodyType<TLSaveProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof saveProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSaveProgramQueryOptions({ programId }, tLSaveProgramRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Save Program
 */
export const invalidateSaveProgram = async (
  queryClient: QueryClient,
  { programId }: SaveProgramPathParameters,
  tLSaveProgramRequest: BodyType<TLSaveProgramRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSaveProgramQueryKey({ programId }, tLSaveProgramRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Presigned Url
 */
export const getPresignedUrl = (
  { programId }: GetPresignedUrlPathParameters,
  params?: GetPresignedUrlParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDownloadUrlResponse>(
    { url: `/programs/${programId}/download`, method: 'GET', params, signal },
    options,
  );
};

export const getGetPresignedUrlQueryKey = (
  { programId }: GetPresignedUrlPathParameters,
  params?: GetPresignedUrlParams,
) => {
  return ['programs', programId, 'download', ...(params ? [params] : [])] as const;
};

export const getGetPresignedUrlQueryOptions = <
  TData = Awaited<ReturnType<typeof getPresignedUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetPresignedUrlPathParameters,
  params?: GetPresignedUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPresignedUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPresignedUrlQueryKey({ programId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPresignedUrl>>> = ({ signal }) =>
    getPresignedUrl({ programId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getPresignedUrl>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetPresignedUrlQueryResult = NonNullable<Awaited<ReturnType<typeof getPresignedUrl>>>;
export type GetPresignedUrlQueryError = ErrorType<TLHTTPValidationError>;

export function useGetPresignedUrl<
  TData = Awaited<ReturnType<typeof getPresignedUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPresignedUrlPathParameters,
  params: undefined | GetPresignedUrlParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPresignedUrl>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPresignedUrl>>,
          TError,
          Awaited<ReturnType<typeof getPresignedUrl>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPresignedUrl<
  TData = Awaited<ReturnType<typeof getPresignedUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPresignedUrlPathParameters,
  params?: GetPresignedUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPresignedUrl>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPresignedUrl>>,
          TError,
          Awaited<ReturnType<typeof getPresignedUrl>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPresignedUrl<
  TData = Awaited<ReturnType<typeof getPresignedUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPresignedUrlPathParameters,
  params?: GetPresignedUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPresignedUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Presigned Url
 */

export function useGetPresignedUrl<
  TData = Awaited<ReturnType<typeof getPresignedUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetPresignedUrlPathParameters,
  params?: GetPresignedUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPresignedUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPresignedUrlQueryOptions({ programId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Presigned Url
 */
export const prefetchGetPresignedUrlQuery = async <
  TData = Awaited<ReturnType<typeof getPresignedUrl>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: GetPresignedUrlPathParameters,
  params?: GetPresignedUrlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPresignedUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetPresignedUrlQueryOptions({ programId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Presigned Url
 */
export const invalidateGetPresignedUrl = async (
  queryClient: QueryClient,
  { programId }: GetPresignedUrlPathParameters,
  params?: GetPresignedUrlParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetPresignedUrlQueryKey({ programId }, params) }, options);

  return queryClient;
};

/**
 * Get a preview of a program file.
 * @summary Get Program Preview
 */
export const getProgramPreview = (
  { programId }: GetProgramPreviewPathParameters,
  params?: GetProgramPreviewParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPreviewResponse>(
    { url: `/programs/${programId}/preview`, method: 'GET', params, signal },
    options,
  );
};

export const getGetProgramPreviewQueryKey = (
  { programId }: GetProgramPreviewPathParameters,
  params?: GetProgramPreviewParams,
) => {
  return ['programs', programId, 'preview', ...(params ? [params] : [])] as const;
};

export const getGetProgramPreviewQueryOptions = <
  TData = Awaited<ReturnType<typeof getProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetProgramPreviewPathParameters,
  params?: GetProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProgramPreviewQueryKey({ programId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProgramPreview>>> = ({ signal }) =>
    getProgramPreview({ programId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getProgramPreview>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetProgramPreviewQueryResult = NonNullable<Awaited<ReturnType<typeof getProgramPreview>>>;
export type GetProgramPreviewQueryError = ErrorType<TLHTTPValidationError>;

export function useGetProgramPreview<
  TData = Awaited<ReturnType<typeof getProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramPreviewPathParameters,
  params: undefined | GetProgramPreviewParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPreview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgramPreview>>,
          TError,
          Awaited<ReturnType<typeof getProgramPreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProgramPreview<
  TData = Awaited<ReturnType<typeof getProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramPreviewPathParameters,
  params?: GetProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPreview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgramPreview>>,
          TError,
          Awaited<ReturnType<typeof getProgramPreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProgramPreview<
  TData = Awaited<ReturnType<typeof getProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramPreviewPathParameters,
  params?: GetProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Program Preview
 */

export function useGetProgramPreview<
  TData = Awaited<ReturnType<typeof getProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetProgramPreviewPathParameters,
  params?: GetProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProgramPreviewQueryOptions({ programId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Program Preview
 */
export const prefetchGetProgramPreviewQuery = async <
  TData = Awaited<ReturnType<typeof getProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: GetProgramPreviewPathParameters,
  params?: GetProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetProgramPreviewQueryOptions({ programId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Program Preview
 */
export const invalidateGetProgramPreview = async (
  queryClient: QueryClient,
  { programId }: GetProgramPreviewPathParameters,
  params?: GetProgramPreviewParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetProgramPreviewQueryKey({ programId }, params) }, options);

  return queryClient;
};

/**
 * Search a text/code program preview, returning matching line offsets.
 * @summary Search Program Preview
 */
export const searchProgramPreview = (
  { programId }: SearchProgramPreviewPathParameters,
  params: SearchProgramPreviewParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPreviewSearchResponse>(
    { url: `/programs/${programId}/preview/search`, method: 'GET', params, signal },
    options,
  );
};

export const getSearchProgramPreviewQueryKey = (
  { programId }: SearchProgramPreviewPathParameters,
  params?: SearchProgramPreviewParams,
) => {
  return ['programs', programId, 'preview', 'search', ...(params ? [params] : [])] as const;
};

export const getSearchProgramPreviewQueryOptions = <
  TData = Awaited<ReturnType<typeof searchProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: SearchProgramPreviewPathParameters,
  params: SearchProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchProgramPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchProgramPreviewQueryKey({ programId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchProgramPreview>>> = ({ signal }) =>
    searchProgramPreview({ programId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof searchProgramPreview>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SearchProgramPreviewQueryResult = NonNullable<Awaited<ReturnType<typeof searchProgramPreview>>>;
export type SearchProgramPreviewQueryError = ErrorType<TLHTTPValidationError>;

export function useSearchProgramPreview<
  TData = Awaited<ReturnType<typeof searchProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchProgramPreviewPathParameters,
  params: SearchProgramPreviewParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchProgramPreview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchProgramPreview>>,
          TError,
          Awaited<ReturnType<typeof searchProgramPreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchProgramPreview<
  TData = Awaited<ReturnType<typeof searchProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchProgramPreviewPathParameters,
  params: SearchProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchProgramPreview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchProgramPreview>>,
          TError,
          Awaited<ReturnType<typeof searchProgramPreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchProgramPreview<
  TData = Awaited<ReturnType<typeof searchProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchProgramPreviewPathParameters,
  params: SearchProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchProgramPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search Program Preview
 */

export function useSearchProgramPreview<
  TData = Awaited<ReturnType<typeof searchProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: SearchProgramPreviewPathParameters,
  params: SearchProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchProgramPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchProgramPreviewQueryOptions({ programId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Search Program Preview
 */
export const prefetchSearchProgramPreviewQuery = async <
  TData = Awaited<ReturnType<typeof searchProgramPreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: SearchProgramPreviewPathParameters,
  params: SearchProgramPreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchProgramPreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSearchProgramPreviewQueryOptions({ programId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Search Program Preview
 */
export const invalidateSearchProgramPreview = async (
  queryClient: QueryClient,
  { programId }: SearchProgramPreviewPathParameters,
  params: SearchProgramPreviewParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSearchProgramPreviewQueryKey({ programId }, params) }, options);

  return queryClient;
};

/**
 * Rename a specific program.
 * @summary Rename Program
 */
export const renameProgram = (
  { programId }: RenameProgramPathParameters,
  tLRenameProgramRequest: BodyType<TLRenameProgramRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProgramOperationResponse>(
    {
      url: `/programs/${programId}/rename`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLRenameProgramRequest,
      signal,
    },
    options,
  );
};

export const getRenameProgramQueryKey = (
  { programId }: RenameProgramPathParameters,
  tLRenameProgramRequest?: BodyType<TLRenameProgramRequest>,
) => {
  return ['PATCH', 'programs', programId, 'rename', tLRenameProgramRequest] as const;
};

export const getRenameProgramQueryOptions = <
  TData = Awaited<ReturnType<typeof renameProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: RenameProgramPathParameters,
  tLRenameProgramRequest: BodyType<TLRenameProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRenameProgramQueryKey({ programId }, tLRenameProgramRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof renameProgram>>> = ({ signal }) =>
    renameProgram({ programId }, tLRenameProgramRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof renameProgram>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RenameProgramQueryResult = NonNullable<Awaited<ReturnType<typeof renameProgram>>>;
export type RenameProgramQueryError = ErrorType<TLHTTPValidationError>;

export function useRenameProgram<
  TData = Awaited<ReturnType<typeof renameProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RenameProgramPathParameters,
  tLRenameProgramRequest: BodyType<TLRenameProgramRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProgram>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof renameProgram>>,
          TError,
          Awaited<ReturnType<typeof renameProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRenameProgram<
  TData = Awaited<ReturnType<typeof renameProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RenameProgramPathParameters,
  tLRenameProgramRequest: BodyType<TLRenameProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProgram>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof renameProgram>>,
          TError,
          Awaited<ReturnType<typeof renameProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRenameProgram<
  TData = Awaited<ReturnType<typeof renameProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RenameProgramPathParameters,
  tLRenameProgramRequest: BodyType<TLRenameProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Rename Program
 */

export function useRenameProgram<
  TData = Awaited<ReturnType<typeof renameProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: RenameProgramPathParameters,
  tLRenameProgramRequest: BodyType<TLRenameProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRenameProgramQueryOptions({ programId }, tLRenameProgramRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Rename Program
 */
export const prefetchRenameProgramQuery = async <
  TData = Awaited<ReturnType<typeof renameProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: RenameProgramPathParameters,
  tLRenameProgramRequest: BodyType<TLRenameProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRenameProgramQueryOptions({ programId }, tLRenameProgramRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Rename Program
 */
export const invalidateRenameProgram = async (
  queryClient: QueryClient,
  { programId }: RenameProgramPathParameters,
  tLRenameProgramRequest: BodyType<TLRenameProgramRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRenameProgramQueryKey({ programId }, tLRenameProgramRequest) },
    options,
  );

  return queryClient;
};

/**
 * Update the contents of a program.
 * @summary Update Program
 */
export const updateProgram = (
  { programId }: UpdateProgramPathParameters,
  tLUpdateProgramRequest: BodyType<TLUpdateProgramRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUpdateProgramResponse>(
    {
      url: `/programs/${programId}/update`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdateProgramRequest,
      signal,
    },
    options,
  );
};

export const getUpdateProgramQueryKey = (
  { programId }: UpdateProgramPathParameters,
  tLUpdateProgramRequest?: BodyType<TLUpdateProgramRequest>,
) => {
  return ['PUT', 'programs', programId, 'update', tLUpdateProgramRequest] as const;
};

export const getUpdateProgramQueryOptions = <
  TData = Awaited<ReturnType<typeof updateProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: UpdateProgramPathParameters,
  tLUpdateProgramRequest: BodyType<TLUpdateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateProgramQueryKey({ programId }, tLUpdateProgramRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateProgram>>> = ({ signal }) =>
    updateProgram({ programId }, tLUpdateProgramRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateProgram>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateProgramQueryResult = NonNullable<Awaited<ReturnType<typeof updateProgram>>>;
export type UpdateProgramQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateProgram<
  TData = Awaited<ReturnType<typeof updateProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateProgramPathParameters,
  tLUpdateProgramRequest: BodyType<TLUpdateProgramRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProgram>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateProgram>>,
          TError,
          Awaited<ReturnType<typeof updateProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateProgram<
  TData = Awaited<ReturnType<typeof updateProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateProgramPathParameters,
  tLUpdateProgramRequest: BodyType<TLUpdateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProgram>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateProgram>>,
          TError,
          Awaited<ReturnType<typeof updateProgram>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateProgram<
  TData = Awaited<ReturnType<typeof updateProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateProgramPathParameters,
  tLUpdateProgramRequest: BodyType<TLUpdateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Program
 */

export function useUpdateProgram<
  TData = Awaited<ReturnType<typeof updateProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: UpdateProgramPathParameters,
  tLUpdateProgramRequest: BodyType<TLUpdateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateProgramQueryOptions({ programId }, tLUpdateProgramRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Program
 */
export const prefetchUpdateProgramQuery = async <
  TData = Awaited<ReturnType<typeof updateProgram>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: UpdateProgramPathParameters,
  tLUpdateProgramRequest: BodyType<TLUpdateProgramRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProgram>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateProgramQueryOptions({ programId }, tLUpdateProgramRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Program
 */
export const invalidateUpdateProgram = async (
  queryClient: QueryClient,
  { programId }: UpdateProgramPathParameters,
  tLUpdateProgramRequest: BodyType<TLUpdateProgramRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateProgramQueryKey({ programId }, tLUpdateProgramRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Program Version History
 */
export const getProgramVersionHistory = (
  { programId }: GetProgramVersionHistoryPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProgramVersionHistoryResponse>(
    { url: `/programs/${programId}/versions`, method: 'GET', signal },
    options,
  );
};

export const getGetProgramVersionHistoryQueryKey = ({ programId }: GetProgramVersionHistoryPathParameters) => {
  return ['programs', programId, 'versions'] as const;
};

export const getGetProgramVersionHistoryQueryOptions = <
  TData = Awaited<ReturnType<typeof getProgramVersionHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetProgramVersionHistoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramVersionHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProgramVersionHistoryQueryKey({ programId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProgramVersionHistory>>> = ({ signal }) =>
    getProgramVersionHistory({ programId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getProgramVersionHistory>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetProgramVersionHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof getProgramVersionHistory>>>;
export type GetProgramVersionHistoryQueryError = ErrorType<TLHTTPValidationError>;

export function useGetProgramVersionHistory<
  TData = Awaited<ReturnType<typeof getProgramVersionHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramVersionHistoryPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramVersionHistory>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgramVersionHistory>>,
          TError,
          Awaited<ReturnType<typeof getProgramVersionHistory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProgramVersionHistory<
  TData = Awaited<ReturnType<typeof getProgramVersionHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramVersionHistoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramVersionHistory>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgramVersionHistory>>,
          TError,
          Awaited<ReturnType<typeof getProgramVersionHistory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProgramVersionHistory<
  TData = Awaited<ReturnType<typeof getProgramVersionHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramVersionHistoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramVersionHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Program Version History
 */

export function useGetProgramVersionHistory<
  TData = Awaited<ReturnType<typeof getProgramVersionHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetProgramVersionHistoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramVersionHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProgramVersionHistoryQueryOptions({ programId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Program Version History
 */
export const prefetchGetProgramVersionHistoryQuery = async <
  TData = Awaited<ReturnType<typeof getProgramVersionHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: GetProgramVersionHistoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramVersionHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetProgramVersionHistoryQueryOptions({ programId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Program Version History
 */
export const invalidateGetProgramVersionHistory = async (
  queryClient: QueryClient,
  { programId }: GetProgramVersionHistoryPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetProgramVersionHistoryQueryKey({ programId }) }, options);

  return queryClient;
};
